import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { CXButton, CXDatePicker } from '../../components/CXForm';
import SimOFPTable, { SortBy, SortOrder } from '../../components/Sim/SimOFPTable';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { clearSimOFPForm, selectSim, setSimFlightPlans } from '../../slice/simSlice';
import { getSimPlans, getSimTemplateUfiList, getSimTemplateTagList } from '../../services/simAPI';
import { pushToast, selectApp, setAppFlags } from '../../slice/appSlice';
import AddOrEditOFPLightBox from '../../components/Sim/AddOrEditOFPLightBox';
import Access from '../../components/Access';
import { AccessType } from '../../constants/roleAccessConstant';
import { useLocation } from 'react-router-dom';

type PagedStringList = {
  records: string[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const DEFAULT_PAGED_LIST: PagedStringList = {
  records: [],
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const STORAGE_CLIENT_SORT_KEY = 'simLanding.clientSortState';
const STORAGE_RESTORE_ON_NEXT_MOUNT_KEY = 'simLanding.restoreOnNextMount';

type PersistedClientSortState = {
  sortBy?: SortBy;
  sortOrder?: SortOrder;
};

const readPersistedClientSortState = (): PersistedClientSortState => {
  try {
    const raw = sessionStorage.getItem(STORAGE_CLIENT_SORT_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    const sortBy = parsed?.sortBy as SortBy | undefined;
    const sortOrder = parsed?.sortOrder as SortOrder | undefined;

    const sortByValid =
      sortBy === 'std' ||
      sortBy === 'ufi' ||
      sortBy === 'aircraftType' ||
      sortBy === 'aircraftReg' ||
      sortBy === 'status';
    const sortOrderValid = sortOrder === 'asc' || sortOrder === 'desc';

    if (!sortByValid || !sortOrderValid) return {};
    return { sortBy, sortOrder };
  } catch {
    return {};
  }
};

const normalizePagedStringList = (res: any, fallback: PagedStringList): PagedStringList => {
  const data = res?.data ?? res ?? {};
  const records = data?.records ?? data?.data?.records ?? data?.tags ?? data?.ufis ?? [];
  const page = Number(data?.page ?? data?.data?.page ?? fallback.page);
  const limit = Number(data?.limit ?? data?.data?.limit ?? fallback.limit);
  const total = Number(data?.total ?? data?.data?.total ?? fallback.total);
  const totalPages = Number(data?.totalPages ?? data?.data?.totalPages ?? fallback.totalPages);

  return {
    records: Array.isArray(records) ? records.map((v) => String(v || '').trim()).filter(Boolean) : [],
    page: Number.isFinite(page) && page > 0 ? page : fallback.page,
    limit: Number.isFinite(limit) && limit > 0 ? limit : fallback.limit,
    total: Number.isFinite(total) && total >= 0 ? total : fallback.total,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : fallback.totalPages,
  };
};

const SimLandingPage = () => {
  const { pathname: routePath } = useLocation();
  const dispatch = useAppDispatch();
  const { simFlightPlans } = useAppSelector(selectSim);
  const { isAddNewSimOFPLightBoxVisible } = useAppSelector(selectApp);
  const [displayDate, setDisplayDate] = useState(moment().startOf('day').add(8, "hours").toISOString());

  const [allTemplateUfis, setAllTemplateUfis] = useState<string[]>([]);
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [initialTemplateUfiPaged, setInitialTemplateUfiPaged] = useState<PagedStringList>(DEFAULT_PAGED_LIST);
  const [initialTemplateTagPaged, setInitialTemplateTagPaged] = useState<PagedStringList>(DEFAULT_PAGED_LIST);
  const [clientSort, setClientSort] = useState<PersistedClientSortState>({});

  const loadPlans = useCallback(() => {
    return getSimPlans(moment(displayDate).format("YYYY-MM-DD"))
      .then((res) => dispatch(setSimFlightPlans(Object.values(res || {}))))
      .catch(console.log);
  }, [displayDate, dispatch]);

  const prefetchTemplateUfis = useCallback(() => {
    return getSimTemplateUfiList({ page: 1, limit: 25 })
      .then((res) => {
        const normalized = normalizePagedStringList(res, DEFAULT_PAGED_LIST);
        setAllTemplateUfis(normalized.records);
        setInitialTemplateUfiPaged(normalized);
      })
      .catch((err) => {
        console.log(err);
        setAllTemplateUfis([]);
        setInitialTemplateUfiPaged(DEFAULT_PAGED_LIST);
      });
  }, []);

  const prefetchTemplateTags = useCallback(() => {
    return getSimTemplateTagList({ page: 1, limit: 25 })
      .then((res) => {
        const normalized = normalizePagedStringList(res, DEFAULT_PAGED_LIST);
        setTemplateTags(['ALL', ...normalized.records]);
        setInitialTemplateTagPaged(normalized);
      })
      .catch((err) => {
        console.log(err);
        setTemplateTags(['ALL']);
        setInitialTemplateTagPaged(DEFAULT_PAGED_LIST);
      });
  }, []);

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    let timer: number | undefined;
    try {
      const allowRestore = sessionStorage.getItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY) === '1';
      if (!allowRestore) {
        sessionStorage.removeItem(STORAGE_CLIENT_SORT_KEY);
        setClientSort({});
        return;
      }

      setClientSort(readPersistedClientSortState());

      // Delay flag consume to survive React strict-mode remount in dev.
      timer = window.setTimeout(() => {
        try {
          sessionStorage.removeItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY);
        } catch {
          // ignore
        }
      }, 0);
    } catch {
      setClientSort({});
    }

    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      prefetchTemplateUfis();
      prefetchTemplateTags();
    }, 0);

    return () => clearTimeout(t);
  }, [prefetchTemplateUfis, prefetchTemplateTags]);

  const postSubmitAction = () => {
    dispatch(setAppFlags({ field: "isAddNewSimOFPLightBoxVisible", value: false }));
    dispatch(clearSimOFPForm());
    dispatch(pushToast({ type: "success", message: "New SIM OFP added" }));
    loadPlans();
  };

  const AddNewSimOFPButton = () => {
    return (
      <CXButton
        onClick={() => {
          dispatch(clearSimOFPForm());
          dispatch(setAppFlags({ field: "isAddNewSimOFPLightBoxVisible", value: !isAddNewSimOFPLightBoxVisible }));
        }}
        text={"Add New SIM OFP"}
        outline={true}
      />
    );
  };

  const onClientSortChange = useCallback((sortBy?: SortBy, sortOrder?: SortOrder) => {
    setClientSort({ sortBy, sortOrder });

    try {
      if (sortBy && sortOrder) {
        sessionStorage.setItem(STORAGE_CLIENT_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));
      } else {
        sessionStorage.removeItem(STORAGE_CLIENT_SORT_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  return (
    <>
      <div className={"CathaySansBd"} style={{ fontSize: "2rem", lineHeight: "2.25px" }}>Sim OFP</div>
      <div className={"section d-flex justify-content-between"} >
        <div className={"p-2 ps-0"}>
          <CXDatePicker placeholder={"STD"} value={displayDate} onChange={setDisplayDate} zIndex={1029} />
        </div>
        <div className={"p-2"}>
          <CXButton outline={false} text={"Search"} onClick={() => loadPlans()} />
        </div>
        <div className={"p-2 ms-auto"}>
          <Access route={routePath} access={[AccessType.CREATE]}>
            <AddOrEditOFPLightBox
              TriggerButton={AddNewSimOFPButton()}
              postAction={postSubmitAction}
              heading={"Add New SIM OFP"}
              submitButtonText={"Create SIM OFP"}
              allTemplateUfis={allTemplateUfis}
              templateTags={templateTags}
              enablePagedTemplateSelector={true}
              initialTemplateUfiPaged={initialTemplateUfiPaged}
              initialTemplateTagPaged={initialTemplateTagPaged}
            />
          </Access>
        </div>
      </div>

      <div className={"section"}>
        <SimOFPTable
          routePath={routePath}
          simOFPs={simFlightPlans || []}
          clientSortBy={clientSort.sortBy}
          clientSortOrder={clientSort.sortOrder}
          onClientSortChange={onClientSortChange}
        />
      </div>
    </>
  );
};

export default SimLandingPage;
